<template>
  <div class="smp-root">
    <!-- En-tête + bouton ajouter -->
    <div class="smp-header">
      <span class="smp-count">{{ services.length }} service(s)</span>
      <button class="btn-add" @click="openNewService">+ Nouveau service</button>
    </div>

    <div v-if="loading" class="smp-loading">Chargement…</div>
    <div v-else-if="services.length === 0" class="smp-empty">Aucun service configuré.</div>

    <!-- Liste des services -->
    <div v-for="svc in services" :key="svc.id" class="svc-block">
      <div class="svc-row" @click="toggleExpand(svc.id)">
        <div class="svc-info">
          <span class="svc-name">{{ svc.name }}</span>
          <span class="svc-meta">{{ svc.mealPeriodDisplay }} · {{ svc.timeDisplay }}</span>
        </div>
        <div class="svc-actions" @click.stop>
          <button class="btn-icon" title="Modifier" @click="openEditService(svc)">✏️</button>
          <button class="btn-icon btn-danger" title="Supprimer" @click="deleteService(svc)">🗑️</button>
          <span class="svc-chevron">{{ expanded[svc.id] ? "▲" : "▼" }}</span>
        </div>
      </div>

      <!-- Niveaux du service -->
      <div v-if="expanded[svc.id]" class="levels-section">
        <div v-if="!svc.levels || svc.levels.length === 0" class="levels-empty">
          Aucun niveau —
          <button class="btn-link" @click="openNewLevel(svc)">Ajouter</button>
        </div>
        <table v-else class="levels-table">
          <thead>
            <tr>
              <th>Niveau</th>
              <th>Std</th>
              <th>Sans porc</th>
              <th>Végé</th>
              <th>Barquette/pers</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="lvl in svc.levels" :key="lvl.id">
              <td>{{ lvl.levelDisplay }}</td>
              <td>{{ lvl.nbStandardMeals }}</td>
              <td>{{ lvl.nbNoPorkMeals }}</td>
              <td>{{ lvl.nbVegetarianMeals }}</td>
              <td>{{ lvl.nbPersonsPerTray }}</td>
              <td class="level-actions">
                <button class="btn-icon" @click="openEditLevel(svc, lvl)">✏️</button>
                <button class="btn-icon btn-danger" @click="deleteLevel(svc, lvl)">🗑️</button>
              </td>
            </tr>
          </tbody>
        </table>
        <button class="btn-add-level" @click="openNewLevel(svc)">+ Ajouter un niveau</button>
      </div>
    </div>

    <!-- Modal service -->
    <div v-if="svcModal.open" class="smp-overlay" @click.self="svcModal.open = false">
      <div class="smp-modal">
        <h3>{{ svcModal.id ? "Modifier le service" : "Nouveau service" }}</h3>
        <label>
          Nom *
          <input v-model="svcModal.name" class="field" placeholder="Ex : Service maternelle midi" />
        </label>
        <label>
          Période
          <select v-model="svcModal.mealPeriod" class="field">
            <option value="breakfast">Petit-déjeuner</option>
            <option value="lunch">Déjeuner</option>
            <option value="snack">Goûter</option>
            <option value="dinner">Dîner</option>
          </select>
        </label>
        <div class="field-row">
          <label>
            Début
            <input v-model="svcModal.startTime" type="time" class="field" />
          </label>
          <label>
            Fin
            <input v-model="svcModal.endTime" type="time" class="field" />
          </label>
        </div>
        <label>
          Ordre d'affichage
          <input v-model.number="svcModal.order" type="number" class="field" min="0" />
        </label>
        <p v-if="svcModal.error" class="modal-error">{{ svcModal.error }}</p>
        <div class="modal-footer">
          <button class="btn-cancel" @click="svcModal.open = false">Annuler</button>
          <button class="btn-save" :disabled="svcModal.saving" @click="saveService">
            {{ svcModal.saving ? "…" : "Enregistrer" }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal niveau -->
    <div v-if="lvlModal.open" class="smp-overlay" @click.self="lvlModal.open = false">
      <div class="smp-modal">
        <h3>{{ lvlModal.id ? "Modifier le niveau" : "Nouveau niveau" }}</h3>
        <label>
          Niveau *
          <select v-model="lvlModal.level" class="field">
            <optgroup label="Simple">
              <option value="MATERNELLE">Maternelle</option>
              <option value="ELEMENTAIRE">Élémentaire</option>
              <option value="COLLEGE">Collège</option>
              <option value="LYCEE">Lycée</option>
              <option value="ENSEIGNANT">Enseignant</option>
            </optgroup>
            <optgroup label="Primaire détaillé">
              <option value="PS">PS</option>
              <option value="MS">MS</option>
              <option value="GS">GS</option>
              <option value="CP">CP</option>
              <option value="CE1">CE1</option>
              <option value="CE2">CE2</option>
              <option value="CM1">CM1</option>
              <option value="CM2">CM2</option>
              <option value="ANIM-MAT">Animateurs maternelle</option>
              <option value="ANIM-ELE">Animateurs élémentaire</option>
            </optgroup>
          </select>
        </label>
        <div class="field-row">
          <label>
            Repas standard
            <input v-model.number="lvlModal.nbStandardMeals" type="number" class="field" min="0" />
          </label>
          <label>
            Sans porc
            <input v-model.number="lvlModal.nbNoPorkMeals" type="number" class="field" min="0" />
          </label>
        </div>
        <div class="field-row">
          <label>
            Végétarien
            <input v-model.number="lvlModal.nbVegetarianMeals" type="number" class="field" min="0" />
          </label>
          <label>
            Pers./barquette
            <input v-model.number="lvlModal.nbPersonsPerTray" type="number" class="field" min="1" />
          </label>
        </div>
        <p v-if="lvlModal.error" class="modal-error">{{ lvlModal.error }}</p>
        <div class="modal-footer">
          <button class="btn-cancel" @click="lvlModal.open = false">Annuler</button>
          <button class="btn-save" :disabled="lvlModal.saving" @click="saveLevel">
            {{ lvlModal.saving ? "…" : "Enregistrer" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
function camel(obj) {
  if (!obj || typeof obj !== "object") return obj
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [
      k.replace(/_([a-z])/g, (_, c) => c.toUpperCase()),
      Array.isArray(v) ? v.map(camel) : v,
    ])
  )
}

const emptySvc = () => ({
  open: false,
  id: null,
  name: "",
  mealPeriod: "lunch",
  startTime: "",
  endTime: "",
  order: 0,
  error: "",
  saving: false,
})
const emptyLvl = () => ({
  open: false,
  id: null,
  serviceId: null,
  level: "MATERNELLE",
  nbStandardMeals: 0,
  nbNoPorkMeals: 0,
  nbVegetarianMeals: 0,
  nbPersonsPerTray: 1,
  error: "",
  saving: false,
})

export default {
  name: "ServiceManagementPanel",

  props: {
    canteenId: { type: Number, default: null },
  },

  data() {
    return {
      services: [],
      loading: false,
      expanded: {},
      svcModal: emptySvc(),
      lvlModal: emptyLvl(),
    }
  },

  watch: {
    canteenId(v) {
      if (v) this.load()
    },
  },

  mounted() {
    if (this.canteenId) this.load()
  },

  methods: {
    csrf() {
      const m = document.cookie.match(/csrftoken=([^;]+)/)
      return m ? m[1] : ""
    },

    async load() {
      this.loading = true
      try {
        const r = await fetch(`/api/v1/canteen/${this.canteenId}/services/`, { credentials: "same-origin" })
        this.services = r.ok ? (await r.json()).map(camel) : []
      } finally {
        this.loading = false
      }
    },

    toggleExpand(id) {
      this.$set(this.expanded, id, !this.expanded[id])
    },

    // ── Services ──────────────────────────────────────────
    openNewService() {
      this.svcModal = emptySvc()
      this.svcModal.open = true
    },

    openEditService(svc) {
      this.svcModal = {
        open: true,
        saving: false,
        error: "",
        id: svc.id,
        name: svc.name,
        mealPeriod: svc.mealPeriod || "lunch",
        startTime: svc.startTime || "",
        endTime: svc.endTime || "",
        order: svc.order || 0,
      }
    },

    async saveService() {
      if (!this.svcModal.name.trim()) {
        this.svcModal.error = "Le nom est requis."
        return
      }
      this.svcModal.saving = true
      this.svcModal.error = ""
      const body = {
        name: this.svcModal.name,
        meal_period: this.svcModal.mealPeriod,
        start_time: this.svcModal.startTime || null,
        end_time: this.svcModal.endTime || null,
        order: this.svcModal.order,
      }
      const url = this.svcModal.id
        ? `/api/v1/canteen/${this.canteenId}/services/${this.svcModal.id}/`
        : `/api/v1/canteen/${this.canteenId}/services/`
      const method = this.svcModal.id ? "PATCH" : "POST"
      try {
        const r = await fetch(url, {
          method,
          credentials: "same-origin",
          headers: { "Content-Type": "application/json", "X-CSRFToken": this.csrf() },
          body: JSON.stringify(body),
        })
        if (!r.ok) {
          this.svcModal.error = (await r.json()).detail || "Erreur."
          return
        }
        this.svcModal.open = false
        await this.load()
      } finally {
        this.svcModal.saving = false
      }
    },

    async deleteService(svc) {
      if (!confirm(`Supprimer le service "${svc.name}" ?`)) return
      await fetch(`/api/v1/canteen/${this.canteenId}/services/${svc.id}/`, {
        method: "DELETE",
        credentials: "same-origin",
        headers: { "X-CSRFToken": this.csrf() },
      })
      await this.load()
    },

    // ── Niveaux ───────────────────────────────────────────
    openNewLevel(svc) {
      this.lvlModal = emptyLvl()
      this.lvlModal.open = true
      this.lvlModal.serviceId = svc.id
    },

    openEditLevel(svc, lvl) {
      this.lvlModal = {
        open: true,
        saving: false,
        error: "",
        id: lvl.id,
        serviceId: svc.id,
        level: lvl.level,
        nbStandardMeals: lvl.nbStandardMeals,
        nbNoPorkMeals: lvl.nbNoPorkMeals,
        nbVegetarianMeals: lvl.nbVegetarianMeals,
        nbPersonsPerTray: lvl.nbPersonsPerTray,
      }
    },

    async saveLevel() {
      this.lvlModal.saving = true
      this.lvlModal.error = ""
      const body = {
        level: this.lvlModal.level,
        nb_standard_meals: this.lvlModal.nbStandardMeals,
        nb_no_pork_meals: this.lvlModal.nbNoPorkMeals,
        nb_vegetarian_meals: this.lvlModal.nbVegetarianMeals,
        nb_persons_per_tray: this.lvlModal.nbPersonsPerTray,
      }
      const svcId = this.lvlModal.serviceId
      const url = this.lvlModal.id
        ? `/api/v1/canteen/${this.canteenId}/services/${svcId}/levels/${this.lvlModal.id}/`
        : `/api/v1/canteen/${this.canteenId}/services/${svcId}/levels/`
      const method = this.lvlModal.id ? "PATCH" : "POST"
      try {
        const r = await fetch(url, {
          method,
          credentials: "same-origin",
          headers: { "Content-Type": "application/json", "X-CSRFToken": this.csrf() },
          body: JSON.stringify(body),
        })
        if (!r.ok) {
          this.lvlModal.error = (await r.json()).detail || "Erreur."
          return
        }
        this.lvlModal.open = false
        await this.load()
      } finally {
        this.lvlModal.saving = false
      }
    },

    async deleteLevel(svc, lvl) {
      if (!confirm(`Supprimer le niveau "${lvl.levelDisplay}" ?`)) return
      await fetch(`/api/v1/canteen/${this.canteenId}/services/${svc.id}/levels/${lvl.id}/`, {
        method: "DELETE",
        credentials: "same-origin",
        headers: { "X-CSRFToken": this.csrf() },
      })
      await this.load()
    },
  },
}
</script>

<style scoped>
.smp-root {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}
.smp-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.smp-count {
  font-size: 0.8rem;
  color: #6c757d;
}
.smp-loading,
.smp-empty {
  text-align: center;
  color: #adb5bd;
  font-size: 0.85rem;
  padding: 1rem;
}

.svc-block {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
}
.svc-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0.9rem;
  cursor: pointer;
  background: #f8f9fa;
}
.svc-row:hover {
  background: #e9ecef;
}
.svc-info {
  display: flex;
  flex-direction: column;
}
.svc-name {
  font-weight: 600;
  font-size: 0.9rem;
}
.svc-meta {
  font-size: 0.75rem;
  color: #6c757d;
}
.svc-actions {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.svc-chevron {
  font-size: 0.7rem;
  color: #6c757d;
  margin-left: 0.3rem;
}

.levels-section {
  padding: 0.6rem 0.9rem;
  background: #fff;
  border-top: 1px solid #dee2e6;
}
.levels-empty {
  font-size: 0.8rem;
  color: #6c757d;
}
.levels-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
  margin-bottom: 0.5rem;
}
.levels-table th {
  text-align: left;
  padding: 0.3rem 0.4rem;
  color: #6c757d;
  font-weight: 500;
  border-bottom: 1px solid #dee2e6;
}
.levels-table td {
  padding: 0.3rem 0.4rem;
}
.level-actions {
  display: flex;
  gap: 0.3rem;
}

.btn-add {
  padding: 0.35rem 0.7rem;
  background: #4361ee;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 0.8rem;
  cursor: pointer;
}
.btn-add:hover {
  background: #3a56d4;
}
.btn-add-level {
  margin-top: 0.4rem;
  padding: 0.25rem 0.6rem;
  background: transparent;
  color: #4361ee;
  border: 1px dashed #4361ee;
  border-radius: 5px;
  font-size: 0.75rem;
  cursor: pointer;
}
.btn-icon {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0.1rem;
  opacity: 0.7;
}
.btn-icon:hover {
  opacity: 1;
}
.btn-danger:hover {
  color: #dc3545;
}
.btn-link {
  background: none;
  border: none;
  color: #4361ee;
  cursor: pointer;
  font-size: inherit;
  text-decoration: underline;
  padding: 0;
}

.smp-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 900;
}
.smp-modal {
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  width: min(440px, 92vw);
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}
.smp-modal h3 {
  margin: 0;
  font-size: 1rem;
}
.smp-modal label {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.82rem;
  color: #495057;
}
.field {
  padding: 0.4rem 0.6rem;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 0.85rem;
}
.field-row {
  display: flex;
  gap: 0.6rem;
}
.field-row label {
  flex: 1;
}
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}
.modal-error {
  color: #dc3545;
  font-size: 0.8rem;
  margin: 0;
}
.btn-cancel {
  padding: 0.4rem 0.9rem;
  background: #e9ecef;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
}
.btn-save {
  padding: 0.4rem 0.9rem;
  background: #4361ee;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
}
.btn-save:disabled {
  opacity: 0.5;
}
</style>
